import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectItem,
  type SelectProps,
} from '@breezeai-frontend/cargo-ui';
import { useQuery } from '@tanstack/react-query';
import kebabCase from 'lodash-es/kebabCase';

import { usePlatform } from '../../../../context/PlatformContext';
import {
  FilterableFields,
  FilterOperators,
} from '../../../../network/apis/types';
import { type StorageLocationModel } from '../../locations/types';
import { storageLocationQueries } from '../../network/queries';
import { type DeclarationFormData } from './DeclarationCreationModal';

interface LocationSelectProps
  extends Omit<SelectProps<StorageLocationModel>, 'children'> {
  distributorId: DeclarationFormData['distributorId'];
  defaultLocation?: StorageLocationModel | null; // TODO: fix type of storageLocationQueries.details query so null can be removed from type
}

export function LocationSelect({
  distributorId,
  defaultLocation,
  ...props
}: LocationSelectProps) {
  const { t } = useTranslation();
  const { platform } = usePlatform();

  const { data: locations, isLoading } = useQuery(
    storageLocationQueries.locations({
      params: {
        paginate: false,
        filters: [
          {
            field: FilterableFields.DISTRIBUTOR_ID,
            operator: FilterOperators.IN,
            values: distributorId ? [distributorId.toString()] : [],
          },
        ],
      },
      enabled: !!distributorId && !defaultLocation,
    }),
  );

  return (
    <Select
      isLoading={isLoading}
      data-testid="location-select"
      placeholder={t('declarations.form.location.Placeholder', {
        ns: 'storage',
        context: platform,
      })}
      label={t('declarations.form.location.Label', {
        ns: 'storage',
        context: platform,
      })}
      aria-label="Select Location"
      absoluteErrorMessage
      {...props}
    >
      {!distributorId && (
        // eslint-disable-next-line i18next/no-literal-string -- TODO fix localization
        <SelectItem isDisabled>
          Please select a Primary Assured first
        </SelectItem>
      )}

      {defaultLocation ? (
        <SelectItem
          key={defaultLocation.id}
          id={defaultLocation.id}
          textValue={defaultLocation.location_name}
        >
          {defaultLocation.location_name}
        </SelectItem>
      ) : (
        locations?.map(({ location_name, id }) => (
          <SelectItem
            key={id}
            id={id}
            textValue={location_name}
            data-testid={`select-option-${kebabCase(location_name)}`}
          >
            {location_name}
          </SelectItem>
        ))
      )}
    </Select>
  );
}
